import { FC, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import styled from 'styled-components'
import { Box, Text } from '@mrshmllw/smores-react'

import ConnectYourBankSVG from './connectYourBank.svg?react'
import { fetchConnectionAllowed } from './fetchConnectionAllowed'
import { OpenPlaidButton } from './OpenPlaidButton'
import { getResumeToken } from './resumeToken'
import Loader from '../../../../components/Loader'
import { fetchOauthNonce } from '../../../../services/fetchOauthNonce'

import { useIntercom } from 'utils'

type BankConnectionErrorPromptProps = {
  quoteId: string
  header: string
  description: string
  errorReference: string
  updateMode: boolean
  contactUs: boolean
}

export const BankConnectionErrorPrompt: FC<BankConnectionErrorPromptProps> = ({
  quoteId,
  errorReference,
  header,
  description,
  updateMode,
  contactUs,
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const intercom = useIntercom()
  const [connectionNotAllowed, setConnectionNotAllowed] = useState(false)
  const [oauthNonce, setOauthNonce] = useState('')
  const [isLoading, setIsLoading] = useState(true)

  const resumeToken = getResumeToken()

  if (!resumeToken) {
    navigate(`/quote/${quoteId}/error`, { state: { from: location.pathname } })
  }

  useEffect(() => {
    fetchConnectionAllowed(quoteId, setConnectionNotAllowed, navigate)
    fetchOauthNonce(quoteId, setOauthNonce, navigate)
  }, [quoteId, setOauthNonce, navigate])

  useEffect(() => {
    if (oauthNonce && !connectionNotAllowed) setIsLoading(false)
  }, [oauthNonce, connectionNotAllowed])

  if (isLoading || !resumeToken) return <Loader />

  return (
    <Container flex direction="column" justifyContent="center" alignItems="center" maxWidth="520px">
      <Box mb="64px">
        <ConnectYourBankSVG />
      </Box>

      <Box mb="24px">
        <Text tag="h2" typo="heading-large" align="center">
          {header}
        </Text>
      </Box>

      <Box mb="32px">
        <Text tag="p" align="center">
          {description}
        </Text>
      </Box>

      {contactUs && (
        <Box mb="32px">
          <Text tag="p" align="center">
            Seen this error before?{' '}
            <LinkButton onClick={() => intercom.show()}>
              <b>Contact us</b>
            </LinkButton>
          </Text>
        </Box>
      )}

      <OpenPlaidButton
        setIsLoading={setIsLoading}
        oauthNonce={oauthNonce}
        callToAction="Connect bank account"
        postOAuth={false}
        updateMode={updateMode}
        quoteId={quoteId}
        resumeToken={resumeToken}
      />

      <Box mt="8px">
        <Text tag="p" typo="caption" align="center">
          Reference code: {errorReference}
        </Text>
      </Box>
    </Container>
  )
}

const Container = styled(Box)`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`

const LinkButton = styled.button`
  background-color: transparent;
  border: none;
  cursor: pointer;
  text-decoration: underline;
  display: inline;
  margin: 0;
  padding: 0;
`
